import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import AddStudentsModal from 'components/ClassroomsModule/Classrooms/modals/AddStudentsModal';
import ClassroomSettingsModal from 'components/ClassroomsModule/Classrooms/modals/ClassroomSettingsModal';
import EditClassroomModal from 'components/ClassroomsModule/Classrooms/modals/EditClassroomModal';
import ManageClassroomProductsModal from 'components/ClassroomsModule/Classrooms/modals/ManageClassroomProductsModal';
import PrintCertificatesCardsModal from 'components/ClassroomsModule/Classrooms/modals/PrintCertificatesCardsModal';
import PrintStudentLoginCardsModal from 'components/ClassroomsModule/Classrooms/modals/PrintStudentLoginCardsModal';
import { ClassroomModalRouteAction } from 'components/ClassroomsModule/types';
import { getClassroomsMutationCacheUpdate } from 'components/ClassroomsModule/util/update-cache';
import Config from 'config';
import ARCHIVE_CLASSROOM_MUTATION from 'graph/classroom/archive-classroom-mutation';
import DELETE_CLASSROOM_MUTATION from 'graph/classroom/remove-classroom-mutation';
import useStore from 'store';
import { PrivilegedContext } from 'store/user';
import { ActionOption } from 'types';
import { AlertVariant } from 'types/enums';
import { Classroom } from 'types/schema-generated-types';
import useRouteAction from './route-action/use-route-action';
import { ModificationAlertContent } from './sweet-alert/use-modification-alert';

export type UseClassroomActionOptionsMenu = {
  actionOptions: ActionOption[];
  archivedOptions: ActionOption[];
  loading: boolean;
};

export type UseClassroomActionOptionsMenuProps = {
  translationId: string;
  classrooms?: Classroom[];
  adminConsole?: boolean;
};

const { privilegesGroups } = Config;

const useClassroomActionOptionsMenuProps = ({
  translationId,
  classrooms = [],
  adminConsole = false
}: UseClassroomActionOptionsMenuProps): UseClassroomActionOptionsMenu => {
  const { t } = useTranslation(translationId);
  const [
    { user, userPrivilegesCache },
    { openFullModal, openQuarterModal, openAlert, closeModal }
  ] = useStore();

  const [archiveClassroom, { loading: archiveClassroomLoading }] = useMutation(
    ARCHIVE_CLASSROOM_MUTATION,
    getClassroomsMutationCacheUpdate(Number(user.me?.id), adminConsole)
  );
  const [deleteClassroom, { loading: deleteClassroomLoading }] = useMutation(
    DELETE_CLASSROOM_MUTATION,
    getClassroomsMutationCacheUpdate(Number(user.me?.id), adminConsole)
  );

  const getClassName = React.useCallback(
    (event: React.MouseEvent<HTMLLIElement>): string => {
      const classroom = classrooms?.find(({ id }: Classroom) => +id === +event.currentTarget.id);
      return classroom?.name || '';
    },
    [classrooms]
  );

  const deleteClassroomCallback = React.useCallback(
    (title: string, text: string, classroomId: string) => async (): Promise<void> => {
      await deleteClassroom({ variables: { classroomId: Number(classroomId) } });
      openAlert({
        variant: AlertVariant.SUCCESS,
        title,
        text
      });
    },
    [openAlert, deleteClassroom]
  );

  const archiveClassroomCallback = React.useCallback(
    (title: string, text: string, classroomId: string) => async (): Promise<void> => {
      await archiveClassroom({ variables: { classroomId: Number(classroomId) } });
      openAlert({
        variant: AlertVariant.SUCCESS,
        title,
        text
      });
    },
    [openAlert, archiveClassroom]
  );

  const showArchiveAlert = React.useCallback(
    (event: React.MouseEvent<HTMLLIElement>): void => {
      openAlert({
        variant: AlertVariant.CLASSROOM_ARCHIVE,
        textItems: [getClassName(event) || ''],
        onSubmit: archiveClassroomCallback(
          t('successTitle', { ns: 'ArchiveClassroomsAlert' }),
          t('successMessage', { ns: 'ArchiveClassroomsAlert' }),
          event.currentTarget.id
        )
      });
    },
    [openAlert, getClassName, t, archiveClassroomCallback]
  );

  const showDeleteAlert = React.useCallback(
    (event: React.MouseEvent<HTMLLIElement>): void => {
      const textItems = [getClassName(event) ?? ''];
      const deleteText = t('deleteConfirmInstructions', { ns: '@common' });
      const title = t('title', { count: textItems.length, ns: 'DeleteClassroomsAlert' });
      const confirmButtonText = t('confirm', {
        count: textItems.length,
        ns: 'DeleteClassroomsAlert'
      });

      openAlert({
        variant: AlertVariant.DELETE,
        title,
        confirmButtonText,
        deleteText,
        html: <ModificationAlertContent textItems={textItems} deleteText={deleteText} />,
        onSubmit: deleteClassroomCallback(
          t('successTitle', { ns: 'DeleteClassroomsAlert' }),
          t('successMessage', { ns: 'DeleteClassroomsAlert' }),
          event.currentTarget.id
        )
      });
    },
    [openAlert, getClassName, t, deleteClassroomCallback]
  );

  const openPrintLoginCardsModal = (event: React.MouseEvent<HTMLLIElement>): void => {
    openQuarterModal({
      body: <PrintStudentLoginCardsModal />,
      routeAction: ClassroomModalRouteAction.PRINT_STUDENT_LOGIN_CARDS,
      routeParams: { classroomId: Number(event.currentTarget.id) }
    });
  };

  const openPrintCertificatesModal = (event: React.MouseEvent<HTMLLIElement>): void => {
    openQuarterModal({
      body: <PrintCertificatesCardsModal />,
      routeAction: ClassroomModalRouteAction.PRINT_STUDENT_CERTIFICATES,
      routeParams: { classroomId: Number(event.currentTarget.id) }
    });
  };

  const openAddStudentsModal = async (classroomId: string): Promise<void> => {
    openFullModal({
      title: 'Add Students',
      body: <AddStudentsModal onClose={handleClose} />,
      routeAction: ClassroomModalRouteAction.ADD_STUDENTS,
      routeParams: { classroomId }
    });
  };

  const addStudentsClick = (event: React.MouseEvent<HTMLLIElement>): void => {
    openAddStudentsModal(event.currentTarget.id);
  };

  const editClassDetailsClick = async (event: React.MouseEvent<HTMLLIElement>): Promise<void> => {
    openEditClassDetailsModal(event.currentTarget.id);
  };

  const openEditClassDetailsModal = async (classroomId: string): Promise<void> => {
    openQuarterModal({
      body: <EditClassroomModal onClose={handleCloseClassDetailsModal} />,
      routeAction: ClassroomModalRouteAction.EDIT_CLASSROOM,
      routeParams: { classroomId }
    });
  };

  const handleCloseClassDetailsModal = (success?: boolean): void => {
    closeModal();
    if (success) {
      openAlert({
        variant: AlertVariant.SUCCESS,
        title: t('Class Updated'),
        text: t('Your class details have been successfully updated.')
      });
    }
  };

  const editClassProductsClick = (event: React.MouseEvent<HTMLLIElement>): void => {
    openEditClassroomProductsModal(event.currentTarget.id);
  };

  const openEditClassroomProductsModal = async (classroomId: string): Promise<void> => {
    openFullModal({
      body: <ManageClassroomProductsModal onClose={closeModal} />,
      routeAction: ClassroomModalRouteAction.MANAGE_CLASSROOM_PRODUCTS,
      routeParams: { classroomId }
    });
  };

  const openEditClassroomSettingsModal = async (classroomId: number): Promise<void> => {
    openFullModal({
      body: <ClassroomSettingsModal />,
      routeAction: ClassroomModalRouteAction.MANAGE_CLASSROOM_SETTINGS,
      routeParams: { classroomId }
    });
  };

  const handleClose = (success?: boolean): void => {
    closeModal();
    if (success) {
      openAlert({
        variant: AlertVariant.SUCCESS,
        title: t('Students Added'),
        text: t('You successfully added students to your class.'),
        onClose: () => window.location.reload()
      });
    }
  };

  const handleEditClassroomSettingsClick = (event: React.MouseEvent<HTMLElement>): void => {
    openEditClassroomSettingsModal(Number(event.currentTarget.id));
  };

  const archivedOptions = [
    {
      label: 'Delete Class',
      MenuItemProps: {
        onClick: (event: React.MouseEvent<HTMLLIElement>): void => showDeleteAlert(event)
      },
      isPrivileged: ({ classroomId, organizationId }: PrivilegedContext): boolean =>
        userPrivilegesCache.isPrivileged(privilegesGroups.mutateClassroom, {
          classroomId,
          organizationId
        })
    }
  ];

  const actionOptions: ActionOption[] = [
    {
      label: 'Manage Class Settings',
      MenuItemProps: { onClick: handleEditClassroomSettingsClick }
    },
    {
      label: 'Manage Student Products',
      MenuItemProps: { onClick: editClassProductsClick },
      isPrivileged: ({ classroomId, organizationId }: PrivilegedContext): boolean =>
        userPrivilegesCache.isPrivileged(
          [...privilegesGroups.updateClassroomLicense, ...privilegesGroups.updateOrgLicense],
          {
            classroomId,
            organizationId
          }
        )
    },
    {
      label: 'Print Student Login Cards',
      MenuItemProps: {
        onClick: (event: React.MouseEvent<HTMLLIElement>): void => openPrintLoginCardsModal(event)
      }
    },
    {
      label: 'Print Student Certificates',
      MenuItemProps: {
        onClick: openPrintCertificatesModal
      }
    },
    {
      label: 'Edit Class Details',
      MenuItemProps: { onClick: editClassDetailsClick },
      isPrivileged: ({ classroomId, organizationId }: PrivilegedContext): boolean =>
        userPrivilegesCache.isPrivileged(privilegesGroups.mutateClassroom, {
          classroomId,
          organizationId
        })
    },
    {
      label: 'Add Students',
      MenuItemProps: { onClick: addStudentsClick },
      isPrivileged: ({ classroomId, organizationId }: PrivilegedContext): boolean =>
        userPrivilegesCache.isPrivileged(privilegesGroups.mutateClassroom, {
          classroomId,
          organizationId
        })
    },
    {
      label: 'Archive Class',
      MenuItemProps: {
        onClick: (event: React.MouseEvent<HTMLLIElement>): void => showArchiveAlert(event)
      },
      isPrivileged: ({ classroomId, organizationId }: PrivilegedContext): boolean =>
        userPrivilegesCache.isPrivileged(privilegesGroups.mutateClassroom, {
          classroomId,
          organizationId
        })
    },
    ...archivedOptions
  ];

  useRouteAction(({ action, params }) => {
    if (!action) return;

    switch (action) {
      case ClassroomModalRouteAction.EDIT_CLASSROOM:
        return openEditClassDetailsModal(params.classroomId);
      case ClassroomModalRouteAction.MANAGE_CLASSROOM_PRODUCTS:
        return openEditClassroomProductsModal(params.classroomId);
      case ClassroomModalRouteAction.ADD_STUDENTS:
        return openAddStudentsModal(params.classroomId);
      case ClassroomModalRouteAction.MANAGE_CLASSROOM_SETTINGS:
        return openEditClassroomSettingsModal(params.classroomId);
      default:
        return null;
    }
  });

  return {
    actionOptions,
    archivedOptions,
    loading: archiveClassroomLoading || deleteClassroomLoading
  };
};

export default useClassroomActionOptionsMenuProps;
